import { create } from 'zustand';
import { IPublicAvailableSwap } from '@/interfaces/exchangeIntention';
import { ISearchBy } from '@/interfaces/search';
import { exchangeService } from '@/services/exchange.service';

export interface IAvailableListingPlace {
  city: string;
  state: string;
}

export interface IExchangeIntentionState {
  exchanges: IPublicAvailableSwap[];
  availableListingPlaces: IAvailableListingPlace[];
  isLoading: boolean;
  totalPages: number | null;
  totalItems: number;
  currentPage: number;

  setExchanges: (exchanges: IPublicAvailableSwap[]) => void;
  loadMoreExchanges: (pageIdx: number, append: boolean, searchBy?: ISearchBy) => void;
  setIsLoading: (isLoading: boolean) => void;
  setTotalPages: (totalPages: number) => void;
}

function mergeAvailableListingPlaces(
  existing: IAvailableListingPlace[],
  exchanges: IPublicAvailableSwap[]
): IAvailableListingPlace[] {
  const places = new Map(
    existing.map((place) => [`${place.city.toLowerCase()}|${place.state.toLowerCase()}`, place])
  );
  for (const exchange of exchanges) {
    const city = exchange.city?.trim();
    if (!city) {
      continue;
    }
    const state = exchange.state?.trim() || '';
    const key = `${city.toLowerCase()}|${state.toLowerCase()}`;
    if (!places.has(key)) {
      places.set(key, { city, state });
    }
  }
  return Array.from(places.values()).sort((a, b) =>
    a.city.localeCompare(b.city, undefined, { sensitivity: 'base' })
  );
}

export interface IAuthedUserState {
  user: {
    name: string;
    hasResidence: boolean;
  } | null;
  setAuthedUser: (user: { name: string; hasResidence: boolean }) => void;
  setUserHasResidence: (hasResidence: boolean) => void;
  clearAuthedUser: () => void;
}

export const useExchangeStore = create<IExchangeIntentionState>((set, get) => ({
  exchanges: [],
  availableListingPlaces: [],
  isLoading: false,
  totalPages: null,
  totalItems: 0,
  currentPage: 0,

  setExchanges: (exchanges: IPublicAvailableSwap[]) =>
    set((state) => ({
      exchanges,
      availableListingPlaces: mergeAvailableListingPlaces(state.availableListingPlaces, exchanges),
    })),

  loadMoreExchanges: (pageIdx: number = 0, append: boolean = false, searchBy?: ISearchBy) => {
    set({ isLoading: true });

    const currentState = get();
    if (append && currentState.totalPages && pageIdx >= currentState.totalPages) {
      set({ isLoading: false });
      return;
    }

    exchangeService
      .loadExchangeIntentions(searchBy, pageIdx)
      .then(({ exchanges, totalPages, totalItems }) => {
        const state = get();
        set({
          exchanges: append ? [...state.exchanges, ...exchanges] : exchanges,
          availableListingPlaces: mergeAvailableListingPlaces(
            state.availableListingPlaces,
            exchanges
          ),
          totalPages,
          totalItems,
          currentPage: pageIdx,
          isLoading: false,
        });
      })
      .catch((err) => {
        set({ isLoading: false });
        throw err;
      });
  },

  setIsLoading: (isLoading: boolean) =>
    set({
      isLoading,
    }),

  setTotalPages: (totalPages: number) =>
    set({
      totalPages,
    }),
}));

export const useAuthedUserStore = create<IAuthedUserState>((set) => ({
  isLoggedIn: false,
  user: {
    name: '',
    hasResidence: false,
  },

  setAuthedUser: (user: { name: string; hasResidence: boolean }) =>
    set({
      user,
    }),

  setUserHasResidence: (hasResidence: boolean) =>
    set((state) => {
      if (!state.user) {
        return { user: null };
      }
      return {
        user: {
          ...state.user,
          hasResidence,
        },
      };
    }),

  clearAuthedUser: () =>
    set({
      user: null,
    }),
}));
